/**
 * Phase 16 Plan 16-03 — WorkOrdersListPage
 *
 * Route: /work-orders
 * Permission gate: workorders.view
 *
 * List of Work Orders with search, site/status/date filters, pagination.
 * Mirrors SitesListPage.tsx style (Phase 9) with TanStack Table.
 *
 * T-16-21 mitigated: redirect to /dashboard if workorders.view permission absent.
 */

import { PlusIcon } from '@phosphor-icons/react';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { WorkOrderStatusBadge } from '@/components/work-orders/WorkOrderStatusBadge';
import { useWorkOrdersListQuery } from '@/hooks/useWorkOrderQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import { WorkOrderStatus, type WorkOrderListItem, type WorkOrderListQuery } from '@/types/work-orders';

const PAGE_SIZE = 20;

type StatusFilter = WorkOrderStatus | 'all';

function isValidStatus(s: string | null): s is StatusFilter {
  return (
    s === 'all' ||
    s === WorkOrderStatus.Draft ||
    s === WorkOrderStatus.Active ||
    s === WorkOrderStatus.Completed ||
    s === WorkOrderStatus.Cancelled
  );
}

function formatDate(d: string): string {
  // yyyy-MM-dd → locale short date
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return d;
  }
}

export function WorkOrdersListPage(): JSX.Element {
  const { permissions } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQ = searchParams.get('q') ?? '';
  const statusParam = searchParams.get('status');
  const initialStatus: StatusFilter = isValidStatus(statusParam) ? statusParam : 'all';
  const initialPage = (() => {
    const p = Number.parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  })();
  const initialStartFrom = searchParams.get('startDateFrom') ?? '';
  const initialStartTo = searchParams.get('startDateTo') ?? '';

  const [q, setQ] = useState<string>(initialQ);
  const [debouncedQ, setDebouncedQ] = useState<string>(initialQ);
  const [status, setStatus] = useState<StatusFilter>(initialStatus);
  const [startDateFrom, setStartDateFrom] = useState<string>(initialStartFrom);
  const [startDateTo, setStartDateTo] = useState<string>(initialStartTo);
  const [page, setPage] = useState<number>(initialPage);

  // 300ms debounce for search input
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQ(q);
    }, 300);
    return () => {
      window.clearTimeout(t);
    };
  }, [q]);

  // Sync URL search params when state changes
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQ !== '') next.set('q', debouncedQ);
    if (status !== 'all') next.set('status', status);
    if (startDateFrom !== '') next.set('startDateFrom', startDateFrom);
    if (startDateTo !== '') next.set('startDateTo', startDateTo);
    if (page !== 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [debouncedQ, status, startDateFrom, startDateTo, page, setSearchParams]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, status, startDateFrom, startDateTo]);

  const queryParams: WorkOrderListQuery = {
    ...(debouncedQ !== '' ? { q: debouncedQ } : {}),
    ...(status !== 'all' ? { status } : {}),
    ...(startDateFrom !== '' ? { startDateFrom } : {}),
    ...(startDateTo !== '' ? { startDateTo } : {}),
    page,
    pageSize: PAGE_SIZE,
  };

  const workOrdersQuery = useWorkOrdersListQuery(queryParams);

  const columns = useMemo<ColumnDef<WorkOrderListItem>[]>(
    () => [
      {
        accessorKey: 'woNumber',
        header: 'WO Number',
        cell: ({ row }) => (
          <Link
            to={`/work-orders/${String(row.original.id)}`}
            className="text-primary hover:underline font-mono text-xs"
            data-testid={`wo-row-number-${String(row.original.id)}`}
          >
            {row.original.woNumber}
          </Link>
        ),
      },
      {
        id: 'client',
        header: 'Client',
        cell: ({ row }) =>
          row.original.clientId !== undefined ? (
            <Link
              to={`/clients/${String(row.original.clientId)}`}
              className="text-primary hover:underline text-sm"
              data-testid={`wo-row-client-${String(row.original.id)}`}
            >
              {row.original.clientName ?? String(row.original.clientId)}
            </Link>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'sites',
        header: 'Sites',
        cell: ({ row }) => {
          const names = row.original.siteNames ?? [];
          if (names.length === 0) return <span className="text-muted-foreground">—</span>;
          const visible = names.slice(0, 2);
          const extra = names.length - 2;
          return (
            <span className="text-sm">
              {visible.join(', ')}
              {extra > 0 && (
                <span className="text-muted-foreground ml-1">(+{extra} more)</span>
              )}
            </span>
          );
        },
      },
      {
        accessorKey: 'startDate',
        header: 'Start',
        cell: ({ row }) => formatDate(row.original.startDate),
      },
      {
        accessorKey: 'endDate',
        header: 'End',
        cell: ({ row }) => formatDate(row.original.endDate),
      },
      {
        accessorKey: 'machinesCount',
        header: 'Machines',
        cell: ({ row }) =>
          row.original.machinesCount === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span>{row.original.machinesCount}</span>
          ),
      },
      {
        accessorKey: 'kpisCount',
        header: 'KPIs',
        cell: ({ row }) =>
          row.original.kpisCount === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span>{row.original.kpisCount}</span>
          ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <WorkOrderStatusBadge status={row.original.status} />,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link
                to={`/work-orders/${String(row.original.id)}`}
                data-testid={`wo-row-view-${String(row.original.id)}`}
              >
                View
              </Link>
            </Button>
            {canEdit && (
              <Button asChild variant="outline" size="sm">
                <Link
                  to={`/work-orders/${String(row.original.id)}/edit`}
                  data-testid={`wo-row-edit-${String(row.original.id)}`}
                >
                  Edit
                </Link>
              </Button>
            )}
            <Button asChild variant="ghost" size="sm">
              <Link
                to={`/work-orders/${String(row.original.id)}?tab=kpis`}
                data-testid={`wo-row-kpis-${String(row.original.id)}`}
              >
                Manage KPIs
              </Link>
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissions],
  );

  const data = workOrdersQuery.data?.items ?? [];
  const total = workOrdersQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const tableData = data as unknown as WorkOrderListItem[];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!permissions.includes('workorders.view')) {
    return <Navigate to="/dashboard" replace />;
  }

  const canCreate = permissions.includes('workorders.create');
  const canEdit = permissions.includes('workorders.edit');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Work Orders</h1>
          <p className="text-muted-foreground">
            Manage work orders, link machines, and track status.
          </p>
        </div>
        {canCreate && (
          <Button asChild data-testid="wo-add-button">
            <Link to="/work-orders/new">
              <PlusIcon size={16} weight="bold" /> New WO
            </Link>
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Work Orders</CardTitle>
          <CardDescription>
            Search by WO number or client. Filter by status and date range.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px] space-y-1">
              <Label htmlFor="wo-search">Search</Label>
              <Input
                id="wo-search"
                value={q}
                onChange={(e) => { setQ(e.target.value); }}
                placeholder="WO number or client…"
                data-testid="wo-search-input"
              />
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="wo-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => { if (isValidStatus(v)) setStatus(v); }}
              >
                <SelectTrigger id="wo-status" data-testid="wo-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value={WorkOrderStatus.Draft}>Draft</SelectItem>
                  <SelectItem value={WorkOrderStatus.Active}>Active</SelectItem>
                  <SelectItem value={WorkOrderStatus.Completed}>Completed</SelectItem>
                  <SelectItem value={WorkOrderStatus.Cancelled}>Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-44 space-y-1">
              <Label htmlFor="wo-start-from">Start from</Label>
              <Input
                id="wo-start-from"
                type="date"
                value={startDateFrom}
                onChange={(e) => { setStartDateFrom(e.target.value); }}
                data-testid="wo-start-from-input"
              />
            </div>
            <div className="w-44 space-y-1">
              <Label htmlFor="wo-start-to">Start to</Label>
              <Input
                id="wo-start-to"
                type="date"
                value={startDateTo}
                onChange={(e) => { setStartDateTo(e.target.value); }}
                data-testid="wo-start-to-input"
              />
            </div>
          </div>

          {workOrdersQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="wo-loading">
              Loading work orders…
            </p>
          )}
          {workOrdersQuery.isError && (
            <p className="text-sm text-destructive" data-testid="wo-error">
              Failed to load work orders.
            </p>
          )}
          {workOrdersQuery.data && (
            <>
              <div className="overflow-x-auto">
              <Table data-testid="wo-table">
                <TableHeader>
                  {table.getHeaderGroups().map((hg) => (
                    <TableRow key={hg.id}>
                      {hg.headers.map((h) => (
                        <TableHead key={h.id}>
                          {h.isPlaceholder
                            ? null
                            : flexRender(h.column.columnDef.header, h.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="text-center py-8">
                        <span data-testid="wo-empty">No work orders found.</span>
                      </TableCell>
                    </TableRow>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-testid={`wo-row-${String(row.original.id)}`}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-muted-foreground" data-testid="wo-pagination-summary">
                  Page {page} of {pageCount} • {total} total
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { setPage((p) => Math.max(1, p - 1)); }}
                    disabled={page <= 1}
                    data-testid="wo-pagination-prev"
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { setPage((p) => Math.min(pageCount, p + 1)); }}
                    disabled={page >= pageCount}
                    data-testid="wo-pagination-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
